import { useState } from "react";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import FormCheck from "react-bootstrap/FormCheck";
import { BsTag } from "react-icons/bs";

import Translated from "../feature/Translated";
import Spinner from "./../feature/Spinner";
import { useFeedback } from "../../api-new";
import { useTranslation } from "./TranslationProvider";
import LocalStorageUtil from "../../utils/localStorageUtil";
import {
  CONTACT_CATEGORIES,
  FIELD_DEFS,
  EMPTY_FORM,
  isCategoryFormValid,
  isFieldPrefillable,
} from "../../data/contactCategories";

const ContactForm = (props) => {
  const { translate } = useTranslation();
  const selectableCategories = props.selectableCategories || [];
  const hasSelector = selectableCategories.length > 0;
  const selectedCategory =
    props.selectedCategory ||
    props.category ||
    selectableCategories[0] ||
    "general";

  const [form, setForm] = useState(() => {
    const seed = { ...EMPTY_FORM };
    if (props.initialValues) {
      Object.keys(props.initialValues).forEach((key) => {
        if (isFieldPrefillable(key)) {
          seed[key] = props.initialValues[key];
        }
      });
    }
    return seed;
  });

  const feedbackMutation = useFeedback();

  const category = CONTACT_CATEGORIES[selectedCategory];

  const submitContact = () => {
    if (!isCategoryFormValid(selectedCategory, form)) {
      return;
    }

    // Send the category's fields flat under their own names. `extra` carries
    // only local settings, which help identify the user when not logged in.
    const extra = { settings: LocalStorageUtil.getAllLocalSettingsObj() };
    const payload = { category: selectedCategory, extra };
    category.fields.forEach((fieldName) => {
      payload[fieldName] = form[fieldName];
    });

    feedbackMutation.mutate(payload, {
      onSuccess: () => {
        if (props.onSubmitted) {
          props.onSubmitted();
        }
      },
    });
  };

  const handleInputChange = (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const newForm = { ...form, [e.target.name]: value };
    setForm(newForm);
  };

  const handleCategoryChange = (e) => {
    if (props.onCategoryChange) {
      props.onCategoryChange(e.target.value);
    }
  };

  const renderField = (fieldName) => {
    const def = FIELD_DEFS[fieldName];
    if (!def) {
      return null;
    }

    if (def.type === "checkbox") {
      return (
        <FormCheck
          key={fieldName}
          type="checkbox"
          name={fieldName}
          checked={form[fieldName]}
          onChange={handleInputChange}
          label={translate(def.placeholderKey)}
          className="mb-3"
        />
      );
    }

    if (def.type === "textarea") {
      return (
        <Form.Group key={fieldName} controlId={fieldName} className="mb-3">
          <Form.Control
            name={fieldName}
            as="textarea"
            rows={5}
            value={form[fieldName]}
            placeholder={translate(def.placeholderKey)}
            onChange={handleInputChange}
            className="contact-message-text-area"
          />
        </Form.Group>
      );
    }

    const Icon = def.icon;
    const control = (
      <Form.Control
        type="text"
        name={fieldName}
        placeholder={translate(def.placeholderKey)}
        value={form[fieldName]}
        onChange={handleInputChange}
      />
    );

    return (
      <Form.Group key={fieldName} className="mb-3">
        {Icon ? (
          <InputGroup>
            <InputGroup.Text>
              <Icon className="white" />
            </InputGroup.Text>
            {control}
          </InputGroup>
        ) : (
          control
        )}
      </Form.Group>
    );
  };

  const renderForm = () => {
    const errorMessage = feedbackMutation.isError ? (
      <Alert bsstyle="warning">
        <Translated translationKey={feedbackMutation.error.message} />
      </Alert>
    ) : null;

    return (
      <Form>
        <fieldset disabled={feedbackMutation.isPending}>
          {hasSelector && (
            <Form.Group className="mb-3">
              <InputGroup>
                <InputGroup.Text>
                  <BsTag className="white" />
                </InputGroup.Text>
                <Form.Select
                  name="category"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  aria-label={translate("contact_category")}
                >
                  {selectableCategories.map((key) => (
                    <option key={key} value={key}>
                      {translate(CONTACT_CATEGORIES[key].labelKey)}
                    </option>
                  ))}
                </Form.Select>
              </InputGroup>
            </Form.Group>
          )}

          {category.fields.map((fieldName) => renderField(fieldName))}

          <div className="centered">
            {errorMessage}
            <Button
              aria-label="Submit"
              role="presentation"
              id="submit-contact"
              disabled={
                !isCategoryFormValid(selectedCategory, form) ||
                feedbackMutation.isPending
              }
              onClick={() => submitContact()}
            >
              <Translated translationKey={category.submitLabelKey || "send"} />
            </Button>
            {feedbackMutation.isPending ? (
              <Spinner className="primary" />
            ) : null}
          </div>
        </fieldset>
      </Form>
    );
  };

  return <div>{renderForm()}</div>;
};

export default ContactForm;
